/**
 * Domain hooks del dominio financiero (CashSync).
 *
 * Each hook encapsulates the factory's canonical queryKey + the Drizzle query builder:
 * Drizzle, same as `use-lists-query.ts`: consumers never call
 * `createPowerSyncQuery` directly for this data.
 */

import type { Accessor } from "solid-js";
import { keepPreviousData } from "@tanstack/solid-query";
import type { AbstractPowerSyncDatabase } from "@powersync/common";
import type { PowerSyncDrizzleDB } from "@cashsync/powersync";

import {
  type CategoryRow,
  getCategoriesQuery,
  getPeriodSummaryQuery,
  getReconciliationSummaryQuery,
  getTransactionsQuery,
  getWalletBalancesQuery,
  getWalletGroupsQuery,
  getWalletsQuery,
  type PeriodSummaryRow,
  queryKeys,
  type ReconciliationSummaryRow,
  type TransactionFilters,
  type TransactionWithMeta,
  type WalletBalanceRow,
  type WalletWithMeta,
} from "../index";

import { createPowerSyncQuery, DEFAULT_THROTTLE_MS } from "./create-power-sync-query";

interface BaseFinanceQueryOptions {
  db: Accessor<AbstractPowerSyncDatabase | null>;
  drizzle: Accessor<PowerSyncDrizzleDB | null>;
  userId: Accessor<string | undefined>;
  staleTime?: number;
  throttleTime?: number;
}

// ─── Cuentas ──────────────────────────────────────────────────────────────────

export function useWalletsQuery(options: BaseFinanceQueryOptions) {
  return createPowerSyncQuery<WalletWithMeta>({
    queryKey: () => queryKeys.accounts.list(options.userId()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const uid = options.userId();
      return db && uid ? getWalletsQuery(db, uid) : null;
    },
    enabled: () => Boolean(options.userId()) && Boolean(options.drizzle()),
    placeholderData: keepPreviousData,
    staleTime: options.staleTime,
    throttleTime: options.throttleTime ?? DEFAULT_THROTTLE_MS,
  });
}

export function useWalletBalancesQuery(options: BaseFinanceQueryOptions) {
  return createPowerSyncQuery<WalletBalanceRow>({
    queryKey: () => queryKeys.accounts.balances(options.userId()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const uid = options.userId();
      return db && uid ? getWalletBalancesQuery(db, uid) : null;
    },
    enabled: () => Boolean(options.userId()) && Boolean(options.drizzle()),
    placeholderData: keepPreviousData,
    staleTime: options.staleTime,
    throttleTime: options.throttleTime ?? DEFAULT_THROTTLE_MS,
  });
}

interface UseReconciliationSummaryQueryOptions extends BaseFinanceQueryOptions {
  /** Account to reconcile (reactive). Without an account, the query is disabled. */
  walletId: Accessor<string | undefined>;
}

/** Account reconciliation: derived total balance vs verified balance. */
export function useReconciliationSummaryQuery(options: UseReconciliationSummaryQueryOptions) {
  return createPowerSyncQuery<ReconciliationSummaryRow>({
    queryKey: () => queryKeys.accounts.reconciliation(options.userId(), options.walletId()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const uid = options.userId();
      const wid = options.walletId();
      return db && uid && wid ? getReconciliationSummaryQuery(db, uid, wid) : null;
    },
    enabled: () =>
      Boolean(options.userId()) && Boolean(options.drizzle()) && Boolean(options.walletId()),
    placeholderData: keepPreviousData,
    staleTime: options.staleTime,
    throttleTime: options.throttleTime ?? DEFAULT_THROTTLE_MS,
  });
}

export interface WalletGroupRow {
  id: string;
  user_id: string | null;
  name: string | null;
  sort_order: number | null;
  created_at: string | null;
  updated_at: string | null;
}

export function useWalletGroupsQuery(options: BaseFinanceQueryOptions) {
  return createPowerSyncQuery<WalletGroupRow>({
    queryKey: () => queryKeys.accounts.groups(options.userId()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const uid = options.userId();
      return db && uid ? getWalletGroupsQuery(db, uid) : null;
    },
    enabled: () => Boolean(options.userId()) && Boolean(options.drizzle()),
    placeholderData: keepPreviousData,
    staleTime: options.staleTime,
    throttleTime: options.throttleTime ?? DEFAULT_THROTTLE_MS,
  });
}

// ─── Categories ───────────────────────────────────────────────────────────────

export function useCategoriesQuery(options: BaseFinanceQueryOptions) {
  return createPowerSyncQuery<CategoryRow>({
    queryKey: () => queryKeys.categories.list(options.userId()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const uid = options.userId();
      return db && uid ? getCategoriesQuery(db, uid) : null;
    },
    enabled: () => Boolean(options.userId()) && Boolean(options.drizzle()),
    placeholderData: keepPreviousData,
    staleTime: options.staleTime,
    throttleTime: options.throttleTime ?? DEFAULT_THROTTLE_MS,
  });
}

// ─── Transacciones ────────────────────────────────────────────────────────────

interface UseTransactionsQueryOptions extends BaseFinanceQueryOptions {
  /** Reactive filters (accounting range, account, category, text). */
  filters: Accessor<TransactionFilters>;
  /** Incremental limit (infinite scroll). */
  limit?: Accessor<number | undefined>;
}

export function useTransactionsQuery(options: UseTransactionsQueryOptions) {
  return createPowerSyncQuery<TransactionWithMeta>({
    queryKey: () => {
      const limit = options.limit?.();
      const base = { userId: options.userId(), ...options.filters() };
      return limit === undefined
        ? queryKeys.transactions.list(base)
        : queryKeys.transactions.listPaged(base, limit);
    },
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const uid = options.userId();
      if (!db || !uid) {
        return null;
      }
      return getTransactionsQuery(db, uid, {
        ...options.filters(),
        limit: options.limit?.(),
      });
    },
    enabled: () => Boolean(options.userId()) && Boolean(options.drizzle()),
    placeholderData: keepPreviousData,
    staleTime: options.staleTime,
    throttleTime: options.throttleTime ?? DEFAULT_THROTTLE_MS,
  });
}

interface UsePeriodSummaryQueryOptions extends BaseFinanceQueryOptions {
  fromISO: Accessor<string>;
  toISO: Accessor<string>;
}

export function usePeriodSummaryQuery(options: UsePeriodSummaryQueryOptions) {
  return createPowerSyncQuery<PeriodSummaryRow>({
    queryKey: () =>
      queryKeys.transactions.summary(options.userId(), options.fromISO(), options.toISO()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const uid = options.userId();
      return db && uid ? getPeriodSummaryQuery(db, uid, options.fromISO(), options.toISO()) : null;
    },
    enabled: () => Boolean(options.userId()) && Boolean(options.drizzle()),
    placeholderData: keepPreviousData,
    staleTime: options.staleTime,
    throttleTime: options.throttleTime ?? DEFAULT_THROTTLE_MS,
  });
}
